import { lazy, Suspense } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { SpeedInsights } from "@vercel/speed-insights/react";
import Index from "./pages/Index";

const LazyToaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const LazySonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));

// Lazy-load all non-homepage routes for code splitting
const NotFound = lazy(() => import("./pages/NotFound"));
const FrictionAudit = lazy(() => import("./pages/FrictionAudit"));
const ROICalculator = lazy(() => import("./pages/ROICalculator"));
const JourneyMapper = lazy(() => import("./pages/JourneyMapper"));
const ConversionOptimizer = lazy(() => import("./pages/ConversionOptimizer"));
const GrowthCopilot = lazy(() => import("./pages/GrowthCopilot"));
const TrendPredictor = lazy(() => import("./pages/TrendPredictor"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const CaseStudies = lazy(() => import("./pages/CaseStudies"));
const SaaSConversionLift = lazy(() => import("./pages/case-studies/SaaSConversionLift"));
const EcommerceLTVGrowth = lazy(() => import("./pages/case-studies/EcommerceLTVGrowth"));
const FintechARRGrowth = lazy(() => import("./pages/case-studies/FintechARRGrowth"));
const RVDealershipGrowth = lazy(() => import("./pages/case-studies/RVDealershipGrowth"));
const OutdoorApparelBrand = lazy(() => import("./pages/case-studies/OutdoorApparelBrand"));
const HealthTechPatientEngagement = lazy(() => import("./pages/case-studies/HealthTechPatientEngagement"));
const AutomotiveFortune500 = lazy(() => import("./pages/case-studies/AutomotiveFortune500"));
const Auth = lazy(() => import("./pages/Auth"));
const Tools = lazy(() => import("./pages/Tools"));
const KnowledgeBase = lazy(() => import("./pages/KnowledgeBase"));
const AIAdvisoryModel = lazy(() => import("./pages/AIAdvisoryModel"));
const Framework = lazy(() => import("./pages/Framework"));
const AISystems = lazy(() => import("./pages/AISystems"));
const Insights = lazy(() => import("./pages/Insights"));
const DecayModel = lazy(() => import("./pages/DecayModel"));
const MahiIndex = lazy(() => import("./pages/MahiIndex"));
const AimriWpPaper = lazy(() => import("./pages/research/AimriWpPaper"));
const Strategy = lazy(() => import("./pages/Strategy"));
const Research = lazy(() => import("./pages/Research"));
const AimriTechPaper = lazy(() => import("./pages/research/AimriTechPaper"));
const Aimri202602Paper = lazy(() => import("./pages/research/Aimri202602Paper"));
const HowIWork = lazy(() => import("./pages/HowIWork"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const AgentSummary = lazy(() => import("./pages/AgentSummary"));
const PrerenderTest = lazy(() => import("./pages/PrerenderTest"));
const BehavioralCMO = lazy(() => import("./pages/expertise/BehavioralCMO"));
const IndependentAIMarketingResearcher = lazy(() => import("./pages/expertise/IndependentAIMarketingResearcher"));
const AIArchitectForMarketing = lazy(() => import("./pages/expertise/AIArchitectForMarketing"));
const queryClient = new QueryClient();

const App = () => {

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Suspense fallback={null}>
          <LazyToaster />
          <LazySonner />
        </Suspense>
        <SpeedInsights />
        <BrowserRouter>
          <Suspense fallback={<div className="min-h-screen bg-background" />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/tools" element={<Tools />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/ai-systems/friction-audit" element={<FrictionAudit />} />
              <Route path="/ai-systems/roi-calculator" element={<ROICalculator />} />
              <Route path="/ai-systems/journey-mapper" element={<JourneyMapper />} />
              <Route path="/ai-systems/conversion-optimizer" element={<ConversionOptimizer />} />
              <Route path="/ai-systems/growth-copilot" element={<GrowthCopilot />} />
              <Route path="/ai-systems/trend-predictor" element={<TrendPredictor />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/case-studies" element={<CaseStudies />} />
              <Route path="/case-studies/saas-conversion-lift" element={<SaaSConversionLift />} />
              <Route path="/case-studies/ecommerce-ltv-growth" element={<EcommerceLTVGrowth />} />
              <Route path="/case-studies/fintech-arr-growth" element={<FintechARRGrowth />} />
              <Route path="/case-studies/rv-dealership-growth" element={<RVDealershipGrowth />} />
              <Route path="/case-studies/outdoor-apparel-brand" element={<OutdoorApparelBrand />} />
              <Route path="/case-studies/healthtech-patient-engagement" element={<HealthTechPatientEngagement />} />
              <Route path="/case-studies/automotive-fortune-500" element={<AutomotiveFortune500 />} />
              <Route path="/knowledge/:frameworkId" element={<KnowledgeBase />} />
              <Route path="/ai-advisory-model" element={<AIAdvisoryModel />} />
              <Route path="/framework" element={<Framework />} />
              <Route path="/ai-systems" element={<AISystems />} />
              <Route path="/about/insights" element={<Insights />} />
              <Route path="/decay-model" element={<DecayModel />} />
              <Route path="/mahi-index" element={<MahiIndex />} />
              <Route path="/academic-research" element={<Navigate to="/research" replace />} />
              <Route path="/research/aimri-wp-2026-01" element={<AimriWpPaper />} />
              <Route path="/strategy" element={<Strategy />} />
              <Route path="/research" element={<Research />} />
              <Route path="/research/aimri-2024-001-tech" element={<AimriTechPaper />} />
              <Route path="/research/aimri-2026-02" element={<Aimri202602Paper />} />
              <Route path="/engagement-protocol" element={<HowIWork />} />
              <Route path="/how-i-work" element={<Navigate to="/engagement-protocol" replace />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/agent-summary" element={<AgentSummary />} />
              <Route path="/prerender-test" element={<PrerenderTest />} />
              <Route path="/expertise/behavioral-cmo" element={<BehavioralCMO />} />
              <Route path="/expertise/independent-ai-marketing-researcher" element={<IndependentAIMarketingResearcher />} />
              <Route path="/expertise/ai-architect-for-marketing" element={<AIArchitectForMarketing />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
